import {
  DIFFICULTY_TIERS,
  type DifficultyDefinition,
  type DifficultyTier,
  type DifficultyTierProfile
} from "./types";

/**
 * Player-count difficulty curve.
 *
 * Authored enemy/boss stats in `content/enemies.json` are the 1-player
 * reference. `tuning.difficulty` then applies:
 *
 *   health = round(base * playerCountMultiplier * tierHealth)
 *   damage = max(1, base + playerCountBonus + tierDamageBonus)
 *   count  = min(maxWaveEnemies, baseCount)
 *
 * Named tiers (Easy, Medium, Hard, TERMINAL) stack after the player-count
 * curve. Medium is an identity profile, so the shipped party numbers stay put.
 * Speed and attack cooldown come from the tier only.
 *
 * Spawn counts stay at the authored solo `baseCount`. `extraPerPlayer` is ignored
 * so a bigger party does not add bodies; HP, damage, and boss knockdown hits carry
 * the extra difficulty.
 *
 * Practice / 1-player rooms share the solo multipliers. Online rooms use the
 * number of players currently in the match (including downed fighters);
 * disconnects and late joiners change the next wave, not enemies already out.
 *
 * Shipped 1 / 4 / 9 preview (see tests/difficulty.test.ts for the lock):
 *   1p  enemy HP ×1.35, boss HP ×1.25, +0 damage
 *   4p  enemy HP ×2.10, boss HP ×4.25, +0 damage
 *   9p  enemy HP ×3.35, boss HP ×6.55, +0 damage
 *  10p  enemy HP ×3.60, boss HP ×7.01, +0 damage
 */
export const MAX_SUPPORTED_PLAYERS = 10;
export const FULL_BOSS_SCALING_EXTRA_PLAYERS = 3;
export const BOSS_KNOCKDOWN_HITS_SOLO = 3;
export const BOSS_KNOCKDOWN_HITS_PARTY = 4;
export const BOSS_KNOCKDOWN_HITS_LARGE_PARTY = 5;
export const LARGE_PARTY_AT_PLAYERS = 5;

export interface DifficultyPreview {
  playerCount: number;
  enemyHealthMultiplier: number;
  bossHealthMultiplier: number;
  enemyDamageBonus: number;
}

export function cappedPlayerCount(playerCount: number): number {
  return Math.min(MAX_SUPPORTED_PLAYERS, Math.max(1, Math.floor(playerCount)));
}

export function sessionPlayerCount(players: Record<string, unknown>): number {
  return cappedPlayerCount(Object.keys(players).length);
}

export function extraPlayers(playerCount: number): number {
  return cappedPlayerCount(playerCount) - 1;
}

export function enemyHealthMultiplier(playerCount: number, difficulty: DifficultyDefinition): number {
  return difficulty.soloEnemyHealth + difficulty.enemyHealthPerExtraPlayer * extraPlayers(playerCount);
}

export function bossHealthMultiplier(playerCount: number, difficulty: DifficultyDefinition): number {
  const extras = extraPlayers(playerCount);
  const fullScalingExtras = Math.min(extras, FULL_BOSS_SCALING_EXTRA_PLAYERS);
  const largePartyExtras = Math.max(0, extras - FULL_BOSS_SCALING_EXTRA_PLAYERS);
  return (
    difficulty.soloBossHealth +
    difficulty.bossHealthPerExtraPlayer * fullScalingExtras +
    difficulty.largePartyBossHealthPerExtraPlayer * largePartyExtras
  );
}

export const DIFFICULTY_TIER_LABEL: Record<DifficultyTier, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  terminal: "TERMINAL"
};

export function isDifficultyTier(value: unknown): value is DifficultyTier {
  return typeof value === "string" && (DIFFICULTY_TIERS as readonly string[]).includes(value);
}

export function normalizeDifficultyTier(value: unknown): DifficultyTier {
  return isDifficultyTier(value) ? value : "medium";
}

export function formatDifficultyTier(tier: DifficultyTier): string {
  return DIFFICULTY_TIER_LABEL[tier].toUpperCase();
}

export function difficultyTierProfile(
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium"
): DifficultyTierProfile {
  return difficulty.tiers[normalizeDifficultyTier(tier)];
}

/** Lives at match start and after a continue. One life means death does not summon you back. */
export function startingLives(
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium"
): number {
  return difficultyTierProfile(difficulty, tier).lives;
}

export function scaledCombatHealth(
  baseHealth: number,
  playerCount: number,
  difficulty: DifficultyDefinition,
  isBoss: boolean,
  tier: DifficultyTier = "medium"
): number {
  const multiplier = isBoss
    ? bossHealthMultiplier(playerCount, difficulty)
    : enemyHealthMultiplier(playerCount, difficulty);
  const tierScale = isBoss
    ? difficultyTierProfile(difficulty, tier).bossHealth
    : difficultyTierProfile(difficulty, tier).enemyHealth;
  return Math.max(1, Math.round(baseHealth * multiplier * tierScale));
}

export function enemyDamageBonus(playerCount: number, difficulty: DifficultyDefinition): number {
  return cappedPlayerCount(playerCount) >= difficulty.enemyDamageBonusAtPlayers
    ? difficulty.enemyDamageBonus
    : 0;
}

export function scaledEnemyDamage(
  baseDamage: number,
  playerCount: number,
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium"
): number {
  return Math.max(
    1,
    baseDamage + enemyDamageBonus(playerCount, difficulty) + difficultyTierProfile(difficulty, tier).damageBonus
  );
}

export function scaledEnemyMoveSpeed(
  baseSpeed: number,
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium"
): number {
  return baseSpeed * difficultyTierProfile(difficulty, tier).speed;
}

/** Non-boss super damage. A fraction of 1, or authored health within the execute cutoff, is a kill. */
export function scaledSpecialDamage(
  scaledMaxHealth: number,
  authoredHealth: number,
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium",
  buff = 1
): number {
  const profile = difficultyTierProfile(difficulty, tier);
  if (profile.specialFraction >= 1 || authoredHealth <= profile.specialExecuteHealth) {
    return scaledMaxHealth;
  }
  return Math.max(1, Math.round(scaledMaxHealth * profile.specialFraction * buff));
}

export function scaledEnemyAttackCooldown(
  baseSeconds: number,
  difficulty: DifficultyDefinition,
  tier: DifficultyTier = "medium"
): number {
  return baseSeconds * difficultyTierProfile(difficulty, tier).cooldown;
}

export function scaledSpawnCount(
  baseCount: number,
  _extraPerPlayer: number,
  _playerCount: number,
  maxWaveEnemies: number
): number {
  return Math.min(maxWaveEnemies, Math.max(0, baseCount));
}

export function bossKnockdownComboHits(playerCount: number): number {
  if (playerCount <= 1) return BOSS_KNOCKDOWN_HITS_SOLO;
  if (playerCount < LARGE_PARTY_AT_PLAYERS) return BOSS_KNOCKDOWN_HITS_PARTY;
  return BOSS_KNOCKDOWN_HITS_LARGE_PARTY;
}

export function difficultyPreview(playerCount: number, difficulty: DifficultyDefinition): DifficultyPreview {
  const cappedCount = cappedPlayerCount(playerCount);
  return {
    playerCount: cappedCount,
    enemyHealthMultiplier: enemyHealthMultiplier(cappedCount, difficulty),
    bossHealthMultiplier: bossHealthMultiplier(cappedCount, difficulty),
    enemyDamageBonus: enemyDamageBonus(cappedCount, difficulty)
  };
}
